import { BrokerStatus, BrokerType, ClientMyBroker, MAX_BROKER_LOOKUP_MCS } from '@numeo/types-core'
import { brokersApi } from 'api/brokers.api'
import { useEffect, useMemo, useSyncExternalStore } from 'react'
import { log } from 'utils/logger'
import { currentBrokerCacheOwner as currentOwner } from './brokerCacheOwner'
import { refetchClassifiedBrokers } from './useClassifiedBrokers'

const BATCH_DELAY_MS = 50
// A failed lookup isn't retried per rendered row; the next request after this long retries it once.
const RETRY_FAILED_AFTER_MS = 30_000
const MAX_PARALLEL_LOOKUPS = 3

// Module-level cache of the caller's brokers, keyed by MC digits. `null` = looked up, not in the list.
// Filled on demand: components ask for the MCs they render and requests in one tick share a lookup.
const entries = new Map<string, ClientMyBroker | null>()
const pending = new Set<string>()
const inFlight = new Set<string>()
const failedAt = new Map<string, number>()
// How many mounted hooks currently want each MC; a refetch only re-requests these.
const mounted = new Map<string, number>()
const queue: string[][] = []
let activeLookups = 0
const listeners = new Set<() => void>()
let flushTimer: ReturnType<typeof setTimeout> | null = null
let snapshot: ClientMyBroker[] = []
let cacheOwner = ''
// Bumped on every account switch; a lookup only touches state from the session that started it.
let session = 0

function syncOwner(): void {
    const owner = currentOwner()
    if (owner === cacheOwner) return
    cacheOwner = owner
    session++
    activeLookups = 0
    mounted.clear()
    entries.clear()
    pending.clear()
    inFlight.clear()
    failedAt.clear()
    queue.length = 0
    snapshot = []
}

/** MC digits as brokers store them (5–7 digits), or null when the value can't be an MC. */
function normalizeMc(mc: string | number | null | undefined): string | null {
    const digits = String(mc ?? '').replace(/\D/g, '')
    return digits.length >= 5 && digits.length <= 7 ? digits : null
}

function notify(): void {
    snapshot = [...entries.values()].filter((item): item is ClientMyBroker => item !== null)
    listeners.forEach((listener) => listener())
}

function scheduleFlush(): void {
    if (!flushTimer) flushTimer = setTimeout(flush, BATCH_DELAY_MS)
}

function flush(): void {
    flushTimer = null
    const batch = [...pending]
    pending.clear()
    for (let i = 0; i < batch.length; i += MAX_BROKER_LOOKUP_MCS) {
        const chunk = batch.slice(i, i + MAX_BROKER_LOOKUP_MCS)
        chunk.forEach((mc) => inFlight.add(mc))
        queue.push(chunk)
    }
    drain()
}

function drain(): void {
    while (activeLookups < MAX_PARALLEL_LOOKUPS && queue.length) {
        const chunk = queue.shift() as string[]
        const started = session
        activeLookups++
        brokersApi
            .lookupBrokers(chunk)
            .then((items) => {
                if (started !== session) return
                const found = new Map(items.map((item) => [normalizeMc(item.mcNumber), item]))
                chunk.forEach((mc) => {
                    entries.set(mc, found.get(mc) ?? null)
                    failedAt.delete(mc)
                })
                notify()
            })
            .catch((error: unknown) => {
                log.e('[useMyBrokers] broker lookup failed', error)
                if (started !== session) return
                const now = Date.now()
                chunk.forEach((mc) => failedAt.set(mc, now))
                notify()
            })
            .finally(() => {
                if (started !== session) return
                activeLookups--
                chunk.forEach((mc) => inFlight.delete(mc))
                drain()
            })
    }
}

function request(mcNumbers: string[]): void {
    syncOwner()
    let added = false
    for (const mc of mcNumbers) {
        if (entries.has(mc) || inFlight.has(mc) || pending.has(mc)) continue
        const failed = failedAt.get(mc)
        if (failed !== undefined && Date.now() - failed < RETRY_FAILED_AFTER_MS) continue
        pending.add(mc)
        added = true
    }
    if (added) scheduleFlush()
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

const getSnapshot = (): ClientMyBroker[] => snapshot

/**
 * The caller's brokers for the given MC numbers, looked up in batches and cached for the session.
 * `brokers` holds every broker looked up so far, so `find`/`some` by MC works for the requested MCs.
 */
export const useMyBrokers = (mcNumbers: ReadonlyArray<string | number | null | undefined> = []) => {
    syncOwner()
    const key = useMemo(() => [...new Set(mcNumbers.map(normalizeMc).filter((mc): mc is string => mc !== null))].sort().join(','), [mcNumbers])
    const wanted = useMemo(() => (key ? key.split(',') : []), [key])

    useEffect(() => {
        if (!wanted.length) return
        wanted.forEach((mc) => mounted.set(mc, (mounted.get(mc) ?? 0) + 1))
        request(wanted)
        return () =>
            wanted.forEach((mc) => {
                const count = (mounted.get(mc) ?? 1) - 1
                if (count > 0) mounted.set(mc, count)
                else mounted.delete(mc)
            })
    }, [wanted])

    const brokers = useSyncExternalStore(subscribe, getSnapshot)
    // A failed MC isn't loading: consumers treat it as unknown (unclassified) instead of spinning.
    const loading = wanted.some((mc) => !entries.has(mc) && !failedAt.has(mc))
    return { brokers, loading }
}

/**
 * Re-looks up the MCs mounted hooks show, e.g. after a bulk import or a red-flag toggle elsewhere, keeping their
 * current values until fresh data arrives. Other cached MCs are dropped and looked up again when next shown.
 */
export function refetchMyBrokers(): void {
    syncOwner()
    for (const mc of [...entries.keys()]) if (!mounted.has(mc)) entries.delete(mc)
    for (const mc of mounted.keys()) {
        failedAt.delete(mc)
        if (!inFlight.has(mc)) pending.add(mc)
    }
    notify()
    if (pending.size) scheduleFlush()
    refetchClassifiedBrokers()
}

/** Merge a broker the server just returned (e.g. from POST /my-brokers) into the cache without a lookup. */
export function upsertMyBroker(broker: ClientMyBroker): void {
    // Brokers without an MC (email-keyed sources) live only in the classified cache, so always refresh it.
    refetchClassifiedBrokers()
    const mc = normalizeMc(broker.mcNumber)
    if (!mc) return
    syncOwner()
    entries.set(mc, broker)
    failedAt.delete(mc)
    notify()
}

/** Optimistically update a single broker's type across all hook instances. */
export function updateMyBrokerStatus(mcNumber: string, type: BrokerType | null, patch?: Partial<ClientMyBroker>): void {
    refetchClassifiedBrokers()
    const mc = normalizeMc(mcNumber)
    if (!mc) return
    syncOwner()
    const current: ClientMyBroker = entries.get(mc) ?? { id: '', brokerId: '', brokerStatus: BrokerStatus.Active, type: BrokerType.UNCLASSIFIED }
    entries.set(mc, { ...current, ...patch, mcNumber: current.mcNumber ?? mc, type: type ?? BrokerType.UNCLASSIFIED })
    notify()
}
